import { useEffect, useRef, useState } from 'react';
import { guestApi, GuestApiError, type GuestApproved } from '../api';
import { FONT_UI, RADIUS, useTheme } from '../theme';
import { BrandLogo } from './BrandLogo';
import { AppBadges } from './AppBadges';
import { Button, Field, Input } from './ui';

// GU1 · Gäst utan inloggning: kod/länk → namn → väntrum → insläppt (eller nekad/utgången).
// Väntrummets id + hemlighet ligger i sessionStorage så att en omladdning inte tappar platsen.

export const GUEST_WAIT_KEY = 'qvenari_guest_wait';
export interface GuestWait { requestId: string; secret: string; name: string }

export function loadGuestWait(): GuestWait | null {
  try { const v = JSON.parse(sessionStorage.getItem(GUEST_WAIT_KEY) || 'null'); return v?.requestId && v?.secret ? v : null; } catch { return null; }
}
export function saveGuestWait(w: GuestWait | null): void {
  try { if (w) sessionStorage.setItem(GUEST_WAIT_KEY, JSON.stringify(w)); else sessionStorage.removeItem(GUEST_WAIT_KEY); } catch { /* privat läge */ }
}

const OUTCOME_TEXT: Record<string, string> = {
  denied: 'Jaktledaren släppte inte in dig den här gången.',
  expired: 'Ingen svarade i tid. Be någon i jakten hålla utkik och försök igen.',
  ended: 'Jakten har avslutats.',
  left: 'Du lämnade väntrummet.',
};

export function GuestJoin({ meetingId, code: initialCode, name: initialName, onJoined, onClose }: {
  meetingId: string | null;
  code: string | null;
  name: string;
  onJoined: (approved: GuestApproved, wait: GuestWait) => void;
  onClose: () => void;
}) {
  const T = useTheme();
  const [code, setCode] = useState(initialCode ?? '');
  const [name, setName] = useState(initialName);
  const [wait, setWait] = useState<GuestWait | null>(() => loadGuestWait());
  const [outcome, setOutcome] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const joinedRef = useRef(false);
  const onJoinedRef = useRef(onJoined);
  onJoinedRef.current = onJoined;

  async function submit() {
    const n = name.trim();
    if (!n) { setErr('Ange ditt namn'); return; }
    if (!meetingId && !code.trim()) { setErr('Ange anslutningskoden'); return; }
    setBusy(true); setErr('');
    try {
      const r = await guestApi.request(meetingId ? { meetingId } : { code: code.trim() }, n);
      const w = { requestId: r.requestId, secret: r.secret, name: n };
      saveGuestWait(w); setWait(w); setOutcome(null);
    } catch (e) {
      setErr((e as Error).message || 'Kunde inte ansluta');
    }
    setBusy(false);
  }

  // Väntrummet: fråga var tredje sekund om någon släppt in oss.
  useEffect(() => {
    if (!wait) return;
    let cancelled = false;
    const check = async () => {
      try {
        const s = await guestApi.status(wait.requestId, wait.secret);
        if (cancelled) return;
        if (s.status === 'approved') {
          if (joinedRef.current) return;
          joinedRef.current = true;
          onJoinedRef.current(s as GuestApproved, wait);
          return;
        }
        if (s.status !== 'pending') { saveGuestWait(null); setWait(null); setOutcome(s.status); }
      } catch (e) {
        if (cancelled) return;
        if (e instanceof GuestApiError && e.status === 404) { saveGuestWait(null); setWait(null); setOutcome('expired'); }
      }
    };
    void check();
    const tid = setInterval(check, 3000);
    return () => { cancelled = true; clearInterval(tid); };
  }, [wait]);

  function leaveWaiting() {
    if (wait) void guestApi.cancel(wait.requestId, wait.secret).catch(() => {});
    saveGuestWait(null); setWait(null); onClose();
  }

  const card: React.CSSProperties = { background: T.surface, border: `1px solid ${T.border}`, borderRadius: RADIUS.xl, boxShadow: T.shadow.raised, padding: 24, width: '100%', maxWidth: 360, textAlign: 'center' };
  return (
    <div style={{ position: 'fixed', inset: 0, background: T.bg, color: T.text, fontFamily: FONT_UI, zIndex: 20000, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 16, overflowY: 'auto' }}>
      <BrandLogo size={40} />
      <div style={{ color: T.textSub, fontSize: 15, margin: '10px 0 28px' }}>{meetingId ? 'Du har blivit inbjuden till en jakt' : 'Anslut till en jakt som gäst'}</div>
      {wait ? (
        <div style={card} role="status" aria-live="polite">
          <span style={{ width: 32, height: 32, border: `3px solid ${T.accentSoft}`, borderTopColor: T.amber, borderRadius: '50%', animation: 'spin 0.8s linear infinite', display: 'inline-block' }} />
          <div style={{ fontWeight: 700, fontSize: 17, margin: '14px 0 6px' }}>Väntar på att bli insläppt</div>
          <div style={{ color: T.textSub, fontSize: 14, lineHeight: 1.45, marginBottom: 18 }}>
            Deltagarna i jakten har fått en notis om att {wait.name} vill ansluta. Håll sidan öppen.
          </div>
          <Button block variant="ghost" onClick={leaveWaiting}>Avbryt</Button>
        </div>
      ) : (
        <form style={card} onSubmit={e => { e.preventDefault(); void submit(); }}>
          {outcome && <div role="alert" style={{ background: T.dangerSoft, color: T.danger, borderRadius: RADIUS.sm, padding: '10px 14px', fontSize: 14, fontWeight: 600, marginBottom: 16 }}>{OUTCOME_TEXT[outcome] ?? 'Du släpptes inte in.'}</div>}
          {!meetingId && (
            <Field label="Anslutningskod" style={{ textAlign: 'left', marginBottom: 12 }}>
              <Input autoFocus type="text" inputMode="text" autoCapitalize="characters" autoComplete="off" value={code}
                onChange={e => setCode(e.target.value.toUpperCase())} placeholder="ABCD-EFGH" maxLength={12} />
            </Field>
          )}
          <Field label="Ditt namn" style={{ textAlign: 'left', marginBottom: 16 }}>
            <Input autoFocus={!!meetingId} type="text" autoComplete="name" value={name} onChange={e => setName(e.target.value)} placeholder="Förnamn Efternamn" maxLength={40} />
          </Field>
          {err && <div role="alert" style={{ color: T.danger, fontSize: 13, fontWeight: 600, marginBottom: 12, textAlign: 'left' }}>{err}</div>}
          <Button type="submit" block size="lg" loading={busy} disabled={!name.trim() || (!meetingId && !code.trim())}>Be om att få ansluta</Button>
          <div style={{ color: T.textMuted, fontSize: 12, lineHeight: 1.4, marginTop: 12 }}>Någon i jakten måste godkänna dig. Du ser kartan, radion och jaktchatten medan jakten pågår.</div>
          <Button block variant="ghost" style={{ marginTop: 8 }} onClick={onClose}>Avbryt</Button>
        </form>
      )}
      {!wait && <AppBadges />}
    </div>
  );
}
